import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Menu, X, Globe, User, LogOut, LayoutDashboard, Plus, ChevronDown, Home, Search, Building2, Heart, Wrench, Calculator, Landmark, MapPin, Map, BarChart3, Sparkles, TrendingUp, Newspaper, BookOpen, Info, Phone, FileText, ArrowRight, ChevronRight } from 'lucide-react';
import { NotificationBell } from '@/components/notifications/NotificationBell';
import { ContactUsButton } from '@/components/support/ContactUsButton';
import { useCurrency } from '@/contexts/CurrencyContext';
import logo from '@/assets/logo.png';

const languages = [
  { code: 'en', label: 'English' },
  { code: 'am', label: 'አማርኛ' },
  { code: 'om', label: 'Afaan Oromoo' },
];

const topBarLinks = [
  { to: '/area-guides', label: 'Area Guide' },
  { to: '#', label: 'Blogs' },
  { to: '#', label: 'Maps' },
];

const categoryNavLinks = [
  { to: '/buy', label: 'HOMES' },
  { to: '/plots', label: 'PLOTS' },
  { to: '/commercial', label: 'COMMERCIAL' },
  { to: '/rent', label: 'RENT' },
  { to: '/agencies', label: 'AGENCIES' },
  { to: '/projects', label: 'NEW PROJECTS' },
];

export function Header() {
  const { t, i18n } = useTranslation();
  const { user, signOut, hasRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const { displayCurrency, setDisplayCurrency, usdToEtb } = useCurrency();

  const canAddProperty = !!user && (hasRole('agent') || hasRole('agency_admin') || hasRole('developer') || hasRole('superadmin'));
  const closeMenu = () => setMobileOpen(false);
  const go = (path: string) => { navigate(path); closeMenu(); };

  const isActive = (path: string) => location.pathname === path.split('?')[0];

  useEffect(() => {
    if (mobileOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [mobileOpen]);

  const fontStyle = { fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif" };

  return (
    <header className="sticky top-0 z-50">
      {/* ── TOP BAR (desktop only - Zameen style) ── */}
      <div className="hidden lg:block bg-primary">
        <div className="container flex h-10 items-center justify-between">
          {/* Left: Utility Links */}
          <nav className="hidden md:flex items-center gap-1">
            {topBarLinks.map((link) => (
              <Link
                key={link.label}
                to={link.to}
                className="px-2.5 py-1 text-[13px] font-medium text-primary-foreground/80 hover:text-primary-foreground transition-colors uppercase tracking-wide"
                style={fontStyle}
              >
                {link.label}
              </Link>
            ))}
            {/* Tools Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-0.5 px-2.5 py-1 text-[13px] font-medium text-primary-foreground/80 hover:text-primary-foreground transition-colors uppercase tracking-wide"
                  style={fontStyle}
                >
                  Tools
                  <ChevronDown className="h-3 w-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="min-w-[160px]">
                <DropdownMenuItem onClick={() => navigate('/cost-calculator')}>
                  Cost Calculator
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/mortgage-calculator')}>
                  Mortgage Calculator
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>

          {/* Right: Lang + Auth */}
          <div className="flex items-center gap-1 ml-auto">
            <ContactUsButton variant="header" />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="hidden sm:inline-flex items-center gap-1 px-2 py-1 text-[12px] font-semibold text-primary-foreground/90 hover:text-primary-foreground hover:bg-primary-foreground/10 rounded transition-colors uppercase tracking-wide"
                  style={fontStyle}
                  aria-label="Change currency"
                >
                  {displayCurrency === 'USD' ? '$' : 'Br'}
                  <ChevronDown className="h-3 w-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[180px]">
                <DropdownMenuItem onClick={() => setDisplayCurrency('ETB')}
                  className={displayCurrency === 'ETB' ? 'bg-primary/10 text-primary font-medium' : ''}>
                  <span className="font-bold mr-2">Br</span> Ethiopian Birr (ETB)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setDisplayCurrency('USD')}
                  className={displayCurrency === 'USD' ? 'bg-primary/10 text-primary font-medium' : ''}>
                  <span className="font-bold mr-2">$</span> US Dollar (USD)
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <div className="px-2 py-1.5 text-[11px] text-muted-foreground">
                  1 USD ≈ Br {usdToEtb.toFixed(2)}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Change language" className="h-7 w-7 text-primary-foreground/80 hover:text-primary-foreground hover:bg-primary-foreground/10">
                  <Globe className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[140px]">
                {languages.map(lang => (
                  <DropdownMenuItem key={lang.code} onClick={() => i18n.changeLanguage(lang.code)}
                    className={i18n.language === lang.code ? 'bg-primary/10 text-primary font-medium' : ''}>
                    {lang.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {user ? (
              <span className="text-[13px] text-primary-foreground/80 px-2 uppercase tracking-wide" style={fontStyle}>
                {t('nav.dashboard')}
              </span>
            ) : (
              <div className="hidden sm:flex items-center gap-0.5">
                <button
                  className="px-2.5 py-1 text-[13px] font-medium text-primary-foreground/80 hover:text-primary-foreground transition-colors uppercase tracking-wide"
                  style={fontStyle}
                  onClick={() => navigate('/login')}
                >
                  {t('nav.login')}
                </button>
                <span className="text-primary-foreground/40 text-[13px]">|</span>
                <button
                  className="px-2.5 py-1 text-[13px] font-medium text-primary-foreground/80 hover:text-primary-foreground transition-colors uppercase tracking-wide"
                  style={fontStyle}
                  onClick={() => navigate('/register')}
                >
                  {t('nav.register')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── BOTTOM BAR ── */}
      <div className="bg-white shadow-md">
        <div className="container flex h-14 md:h-16 lg:h-[77px] items-center justify-between gap-2 px-3 md:px-4 lg:px-6">
          {/* Logo */}
          <Link to="/" className="flex items-center shrink-0 overflow-visible">
            <img
              src={logo}
              alt="EthioRealEstates.com"
              className="h-28 md:h-14 lg:h-52 w-auto object-contain max-w-none"
            />
          </Link>

          {/* Desktop Category Nav */}
          <nav className="hidden lg:flex items-center gap-0">
            {categoryNavLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`px-4 py-4 text-[13px] font-semibold tracking-wide transition-colors border-b-2 ${
                  isActive(link.to)
                    ? 'text-primary border-primary'
                    : 'text-gray-600 border-transparent hover:text-primary hover:border-primary/40'
                }`}
                style={fontStyle}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right Actions */}
          <div className="flex items-center gap-2">
            {user ? (
              <>
                <NotificationBell />
                {(hasRole('agent') || hasRole('agency_admin') || hasRole('developer') || hasRole('superadmin')) && (
                  <Button
                    size="sm"
                    onClick={() => navigate('/dashboard/properties/new')}
                    className="hidden sm:flex gap-1.5 h-8 text-xs font-bold"
                    style={fontStyle}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    {t('nav.postProperty')}
                  </Button>
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" aria-label="User menu" className="h-9 w-9 rounded-full border border-border text-foreground hover:bg-accent">
                      <User className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="min-w-[180px]">
                    <DropdownMenuItem onClick={() => navigate('/dashboard')} className="gap-2">
                      <LayoutDashboard className="h-4 w-4" />
                      {t('nav.dashboard')}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={signOut} className="gap-2 text-destructive">
                      <LogOut className="h-4 w-4" />
                      {t('nav.logout')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <Button
                size="sm"
                className="hidden sm:flex h-9 text-xs font-bold"
                onClick={() => navigate('/register')}
                style={fontStyle}
              >
                {t('nav.register')}
              </Button>
            )}

            <Button
              variant="ghost"
              size="icon"
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              className="lg:hidden h-9 w-9 text-foreground hover:bg-accent"
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>
      </div>

      {/* ── MOBILE MENU ── */}
      {mobileOpen && (
        <>
          <div
            className="lg:hidden fixed inset-0 top-14 z-30 bg-black/40"
            onClick={closeMenu}
            aria-hidden="true"
          />
          <div className="lg:hidden fixed left-0 top-14 bottom-0 z-40 w-[70%] bg-white border-t border-r border-border shadow-xl overflow-y-auto overscroll-contain pb-6 scrollbar-hide" style={fontStyle}>
          {/* Top: auth */}
          <div className="px-4 pt-4 pb-3">
            {user ? (
              <button
                onClick={() => go('/dashboard')}
                className="w-full flex items-center justify-between border border-primary/30 rounded-lg px-4 py-3 text-primary font-semibold text-sm hover:bg-primary/5 transition-colors"
              >
                <span className="flex items-center gap-2">
                  <LayoutDashboard className="h-4 w-4" />
                  {t('nav.dashboard')}
                </span>
                <ArrowRight className="h-4 w-4" />
              </button>
            ) : (
              <button
                onClick={() => go('/register')}
                className="w-full flex items-center justify-between border border-primary/40 rounded-lg px-4 py-3 text-primary font-semibold text-sm hover:bg-primary/5 transition-colors"
              >
                Login or Create Account
                <ArrowRight className="h-4 w-4" />
              </button>
            )}
          </div>

          <div className="border-t border-border" />

          {/* Main nav list */}
          <nav className="py-1">
            <MobileRow icon={Home} label="Home" active={isActive('/')} onClick={() => go('/')} />
            {canAddProperty && (
              <MobileRow icon={Plus} label="Add Property" onClick={() => go('/dashboard/properties/new')} />
            )}
            <MobileRow icon={Search} label="Search Properties" active={isActive('/buy')} onClick={() => go('/buy')} />
            <MobileRow icon={Building2} label="New Projects" active={isActive('/projects')} onClick={() => go('/projects')} />
            <MobileRow icon={Heart} label="Favorites" onClick={() => go(user ? '/dashboard/favorites' : '/login')} />

            {/* Tools collapsible */}
            <button
              onClick={() => setToolsOpen(o => !o)}
              className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-primary/5 transition-colors"
            >
              <Wrench className="h-5 w-5 text-muted-foreground" />
              <span className="flex-1 text-left">Tools</span>
              <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${toolsOpen ? 'rotate-180' : ''}`} />
            </button>
            {toolsOpen && (
              <div className="bg-muted/30">
                <MobileRow icon={Calculator} label="Cost Calculator" indent onClick={() => go('/cost-calculator')} />
                <MobileRow icon={Landmark} label="Home Loan Calculator" indent onClick={() => go('/mortgage-calculator')} />
                <MobileRow icon={MapPin} label="Area Guides" indent onClick={() => go('/area-guides')} />
                <MobileRow icon={Map} label="Society Maps" indent onClick={closeMenu} />
                <MobileRow icon={BarChart3} label="Automated Valuation Models (AVMs)" indent onClick={closeMenu} />
                <MobileRow icon={Sparkles} label="HomeAdvisor AI" indent onClick={closeMenu} />
                <MobileRow icon={TrendingUp} label="Ethio Property Invest" indent onClick={closeMenu} />
              </div>
            )}

            <MobileRow icon={Newspaper} label="News" onClick={() => go('/news')} />
            <MobileRow icon={BookOpen} label="Blog" onClick={() => go('/blogs')} />
          </nav>

          {/* APP CONTROLS */}
          <div className="border-t border-border mt-2 pt-3">
            <div className="px-4 pb-2 text-[11px] font-semibold tracking-widest text-muted-foreground uppercase">
              App Controls
            </div>

            {/* Language */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-primary/5 transition-colors">
                  <Globe className="h-5 w-5 text-muted-foreground" />
                  <span className="flex-1 text-left">
                    {languages.find(l => l.code === i18n.language)?.label || 'English'}
                  </span>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[160px]">
                {languages.map(lang => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => i18n.changeLanguage(lang.code)}
                    className={i18n.language === lang.code ? 'bg-primary/10 text-primary font-medium' : ''}
                  >
                    {lang.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Currency */}
            <button
              onClick={() => setDisplayCurrency(displayCurrency === 'ETB' ? 'USD' : 'ETB')}
              className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-primary/5 transition-colors"
            >
              <span className="h-5 w-5 flex items-center justify-center text-muted-foreground font-bold text-sm">
                {displayCurrency === 'USD' ? '$' : 'Br'}
              </span>
              <span className="flex-1 text-left">Currency</span>
              <span className="text-xs text-muted-foreground">{displayCurrency}</span>
            </button>

            <MobileRow icon={Info} label="About Us" onClick={closeMenu} />
            <MobileRow icon={Phone} label="Contact Us" onClick={() => go('/contact-us')} />
            <MobileRow icon={FileText} label="Terms & Privacy Policy" onClick={closeMenu} />

            {user && (
              <MobileRow
                icon={LogOut}
                label={t('nav.logout')}
                onClick={() => { signOut(); closeMenu(); }}
              />
            )}
          </div>
        </div>
        </>
      )}

    </header>
  );
}

function MobileRow({
  icon: Icon,
  label,
  active,
  indent,
  onClick,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  active?: boolean;
  indent?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 ${indent ? 'pl-12 pr-4' : 'px-4'} py-3 text-sm font-medium transition-colors ${
        active
          ? 'text-primary bg-primary/5 border-l-2 border-primary'
          : 'text-gray-700 hover:bg-primary/5 hover:text-primary'
      }`}
    >
      <Icon className={`h-5 w-5 ${active ? 'text-primary' : 'text-muted-foreground'}`} />
      <span className="flex-1 text-left">{label}</span>
    </button>
  );
}

